/**
 * Fills elevation holes in a planned route so the profile, the ascent total and the saved
 * geometry never show a drop to 0 m that isn't really there.
 *
 * Holes come from the planner's own fallbacks, not from the data sources: a marker is created
 * with `ele: 0` before its elevation is looked up, a provisional straight line is 2D, and an
 * elevation lookup that fails leaves whatever placeholder was there. Each lands as a single
 * vertical spike at a segment boundary — usually where the route leaves the path network.
 *
 * A hole is a missing or non-finite elevation, or an exact 0 that the terrain around it can't
 * explain. A real 0 (a sea-level path) sits next to other low values; a placeholder 0 sits next
 * to 150 m a few metres away. So an exact 0 counts as a hole only when a neighbouring known
 * elevation is further from 0 than both a small tolerance and the horizontal distance to it
 * (a slope steeper than 45°).
 *
 * Holes are filled by linear interpolation along the route between the nearest known
 * elevations, or copied from the only one there is at either end.
 */

export const PLACEHOLDER_ZERO_TOLERANCE_METERS = 10;

const EARTH_RADIUS_METERS = 6371000;

function haversineMeters(a: number[], b: number[]): number {
  const toRad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * toRad;
  const dLon = (b[0] - a[0]) * toRad;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * toRad) * Math.cos(b[1] * toRad) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * A marker's elevation when it has actually been looked up for where it now is, else
 * undefined. Only the coordinate's own z counts: `markerProperties.ele` starts as a
 * placeholder 0 and is left behind when the marker is dragged, while a lookup or a snap
 * writes the z and a move drops it.
 */
export function knownMarkerElevation(marker: any): number | undefined {
  const z = marker?.markerGeometry?.coordinates?.[2];
  return Number.isFinite(z) ? z : undefined;
}

/**
 * Repairs elevation holes across a route's line strings, in walking order, in place.
 * Segments are treated as one line, so a hole at a segment boundary is filled from the
 * segments either side of it. Returns how many vertices were repaired.
 */
export function repairRouteElevations(lines: number[][][]): number {
  const points: number[][] = [];
  for (const line of lines) {
    if (!Array.isArray(line)) continue;
    for (const c of line) if (Array.isArray(c) && c.length >= 2) points.push(c);
  }
  const n = points.length;
  if (n === 0) return 0;

  const along = new Array<number>(n);
  along[0] = 0;
  for (let i = 1; i < n; i++) along[i] = along[i - 1] + haversineMeters(points[i - 1], points[i]);

  // Nearest finite, non-zero elevation on each side: what a 0 has to be plausible against.
  const prevNonZero = new Array<number>(n);
  const nextNonZero = new Array<number>(n);
  let last = -1;
  for (let i = 0; i < n; i++) {
    prevNonZero[i] = last;
    const z = points[i][2];
    if (Number.isFinite(z) && z !== 0) last = i;
  }
  last = -1;
  for (let i = n - 1; i >= 0; i--) {
    nextNonZero[i] = last;
    const z = points[i][2];
    if (Number.isFinite(z) && z !== 0) last = i;
  }

  const implausibleAgainst = (i: number, j: number): boolean => {
    if (j < 0) return false;
    const rise = Math.abs(points[j][2]);
    const run = Math.abs(along[i] - along[j]);
    return rise > PLACEHOLDER_ZERO_TOLERANCE_METERS && rise > run;
  };

  const isHole = (i: number): boolean => {
    const z = points[i][2];
    if (!Number.isFinite(z)) return true;
    if (z !== 0) return false;
    return implausibleAgainst(i, prevNonZero[i]) || implausibleAgainst(i, nextNonZero[i]);
  };

  const hole = new Array<boolean>(n);
  let anyKnown = false;
  let anyHole = false;
  for (let i = 0; i < n; i++) {
    hole[i] = isHole(i);
    if (hole[i]) anyHole = true;
    else anyKnown = true;
  }
  if (!anyHole || !anyKnown) return 0;

  const nextKnown = new Array<number>(n);
  last = -1;
  for (let i = n - 1; i >= 0; i--) {
    nextKnown[i] = last;
    if (!hole[i]) last = i;
  }

  let repaired = 0;
  let prevKnown = -1;
  for (let i = 0; i < n; i++) {
    if (!hole[i]) {
      prevKnown = i;
      continue;
    }
    const p = prevKnown;
    const q = nextKnown[i];
    let z: number;
    if (p >= 0 && q >= 0) {
      const span = along[q] - along[p];
      const t = span > 0 ? (along[i] - along[p]) / span : 0;
      z = points[p][2] + (points[q][2] - points[p][2]) * t;
    } else {
      z = points[p >= 0 ? p : q][2];
    }
    points[i][2] = z;
    repaired++;
  }
  return repaired;
}

/**
 * Gives each marker without a looked-up elevation the elevation of the line where it sits —
 * the start of the segment leaving it, or the end of the one arriving. Keeps the saved
 * `elevationMeters` from being the constructor's placeholder 0. Returns how many were set.
 */
export function syncMarkerElevationsFromLines(markers: any[], lines: number[][][]): number {
  let updated = 0;
  markers.forEach((marker, i) => {
    if (!marker?.markerProperties) return;
    const known = knownMarkerElevation(marker);
    const leaving = lines[i]?.[0]?.[2];
    const arriving = lines[i - 1]?.[lines[i - 1].length - 1]?.[2];
    const z = known ?? (Number.isFinite(leaving) ? leaving : Number.isFinite(arriving) ? arriving : undefined);
    if (z === undefined || marker.markerProperties.ele === z) return;
    marker.markerProperties.ele = z;
    updated++;
  });
  return updated;
}
